import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { X, Share, Plus } from 'lucide-react';

function InstallPrompt() {
  const location = useLocation();
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState('other');

  useEffect(() => {
    setShow(false);

    const dismissed = localStorage.getItem('dimakas_install_dismissed');
    const shownThisSession = sessionStorage.getItem('dimakas_install_session_shown');
    const isStandalone =
      window.navigator.standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches;

    const isDashboard = location.pathname === '/dashboard';

    if (dismissed || shownThisSession || isStandalone || !isDashboard || !user) return;

    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) {
      setPlatform('ios');
    } else if (/Android/.test(ua)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    const timer = setTimeout(() => {
      sessionStorage.setItem('dimakas_install_session_shown', 'true');
      setShow(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, [location.pathname, user]);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem('dimakas_install_dismissed', 'true');
  };

  const hideForNow = () => {
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 top-24 sm:inset-x-auto sm:right-5 sm:top-auto sm:bottom-8 z-[80] flex justify-center sm:justify-end px-4 pointer-events-none" data-testid="install-prompt">
      <div
        className="pointer-events-auto w-full max-w-md max-h-[68vh] overflow-y-auto border border-white/10 bg-black/92 shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur-xl"
        style={{
          borderRadius: '24px',
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.015) 100%)',
          padding: '1.25rem 1.25rem 1.15rem',
        }}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-12 w-12 shrink-0 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center">
              <img
                src="/dimakas-logo.png"
                alt="DIMAKAS"
                style={{ height: 26, width: 'auto' }}
              />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.28em] text-gray-500 mb-1">DIMAKAS APP</p>
              <p className="text-white font-light text-base tracking-wide">
                Πρόσθεσέ το στην αρχική οθόνη
              </p>
              <p className="text-gray-400 text-sm font-light mt-1 leading-relaxed">
                Πιο άμεσο άνοιγμα, πιο smooth εμπειρία, χωρίς να ψάχνεις browser κάθε φορά.
              </p>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="text-gray-500 hover:text-white transition-colors p-1 shrink-0"
            data-testid="install-dismiss-btn"
            aria-label="Κλείσιμο install prompt"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid gap-2.5 mb-4">
          {platform === 'ios' && (
            <>
              <Step num={1}>
                Πάτησε το κουμπί{' '}
                <span className="inline-flex items-center mx-1 px-2 py-0.5 rounded bg-white/10 text-white text-xs">
                  <Share className="w-3 h-3 mr-1" /> Κοινοποίηση
                </span>
                στο Safari
              </Step>
              <Step num={2}>
                Επέλεξε{' '}
                <span className="inline-flex items-center mx-1 px-2 py-0.5 rounded bg-white/10 text-white text-xs">
                  <Plus className="w-3 h-3 mr-1" /> Στην οθόνη αφετηρίας
                </span>
              </Step>
            </>
          )}

          {platform === 'android' && (
            <>
              <Step num={1}>
                Πάτησε τις τρεις τελείες <span className="inline-flex items-center mx-1 px-2 py-0.5 rounded bg-white/10 text-white text-xs">⋮</span>
              </Step>
              <Step num={2}>
                Επέλεξε <span className="px-2 py-0.5 rounded bg-white/10 text-white text-xs mx-1">Προσθήκη στην αρχική οθόνη</span>
              </Step>
            </>
          )}

          {platform === 'desktop' && (
            <>
              <Step num={1}>
                Κάνε κλικ στο εικονίδιο εγκατάστασης <span className="px-2 py-0.5 rounded bg-white/10 text-white text-xs mx-1">⊕</span>
              </Step>
              <Step num={2}>Επέλεξε <strong className="text-white">Εγκατάσταση</strong></Step>
            </>
          )}

          {platform === 'other' && (
            <img
              src="/dimakas-logo.png"
              alt="DIMAKAS"
              style={{ display: 'none' }}
            />
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2.5">
          <button
            onClick={hideForNow}
            className="flex-1 py-3 text-sm font-light tracking-[0.24em] uppercase text-gray-400 hover:text-white border border-white/10 hover:border-white/25 transition-colors"
            data-testid="install-later-btn"
          >
            Αργότερα
          </button>
          <button
            onClick={dismiss}
            className="flex-1 py-3 text-sm font-light tracking-[0.24em] uppercase text-black bg-white hover:bg-zinc-200 transition-colors"
            data-testid="install-never-btn"
          >
            Μην το δείχνεις ξανά
          </button>
        </div>
      </div>
    </div>
  );
}

function Step({ num, children }) {
  return (
    <div className="flex items-start space-x-3">
      <span
        className="flex-shrink-0 flex items-center justify-center text-xs font-light text-black"
        style={{ width: 22, height: 22, borderRadius: '50%', background: 'white', marginTop: 1 }}
      >
        {num}
      </span>
      <p className="text-gray-300 text-sm font-light leading-relaxed">{children}</p>
    </div>
  );
}

export default InstallPrompt;
